import { css } from "dx-styles";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import type { PreviewItem, PreviewParameter, PreviewResult } from "../api/types";
import type { Parameter } from "../lib/document";
import { errorText, muted, row, stack, subheading } from "./primitives";
import { tokens } from "../theme/tokens";

const field = css({
  minHeight: "32px",
  padding: `0 ${tokens.space.xs}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
  width: "100%",
});

const group = css({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.xs,
  paddingTop: tokens.space.xs,
  borderTop: `1px solid ${tokens.color.rule}`,
});

const groupHeader = css({
  display: "flex",
  justifyContent: "space-between",
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: tokens.color.pencil,
});

const itemRow = css({ display: "flex", justifyContent: "space-between", gap: tokens.space.xs });

const quantity = css({ fontFamily: tokens.font.mono, color: tokens.color.pencil });

const stale = css({ opacity: 0.55 });

const warning = css({ color: tokens.color.pencil, fontSize: "13px" });

/** Grouped the way the app groups a checklist. */
const kindOrder: readonly PreviewItem["kind"][] = ["gear", "task", "purchase"];

const kindTitle: Record<PreviewItem["kind"], string> = {
  gear: "Gear",
  task: "Tasks",
  purchase: "To buy",
};

interface Props {
  readonly collectionId: string;
  readonly templateId: string | null;
  readonly parameters: readonly Parameter[];
  /** Bumped by the editor when the saved document changes. */
  readonly documentVersion: number;
  /** True while an edit has not reached the server yet. */
  readonly isDirty: boolean;
  readonly onIssueRules?: (ruleIds: readonly string[]) => void;
}

/**
 * The live panel: parameter controls, the resulting checklist grouped like the
 * app's, and the engine's messages next to the rules they came from.
 *
 * Its whole value is changing while a formula is being typed, so a slow or
 * throttled response must never block the editor: the last good result stays on
 * screen, dimmed, and a stale answer is dropped rather than shown.
 */
export function PreviewPanel({
  collectionId,
  templateId,
  parameters,
  documentVersion,
  isDirty,
  onIssueRules,
}: Props) {
  const [values, setValues] = useState<Record<string, number | boolean | string>>({});
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    setValues({});
    setResult(null);
  }, [templateId]);

  const request = useCallback(
    async (mine: number) => {
      if (!templateId) return;
      try {
        const next = await api.preview(collectionId, templateId, values);
        if (generation.current !== mine) return;
        setResult(next);
        setIsStale(false);
        setProblem(null);
        setRetryAfter(null);
        onIssueRules?.(
          [...next.errors, ...next.warnings]
            .map((issue) => issue.ruleID)
            .filter((id): id is string => typeof id === "string"),
        );
      } catch (caught) {
        if (generation.current !== mine) return;
        if (caught instanceof ApiError && caught.status === 429) {
          // Rate limited: keep the last result, say when it will refresh.
          setIsStale(true);
          setRetryAfter(Number(caught.message.match(/(\d+) seconds/)?.[1] ?? "5"));
          return;
        }
        setIsStale(true);
        setProblem(caught instanceof Error ? caught.message : "Preview failed.");
      }
    },
    [collectionId, templateId, values, onIssueRules],
  );

  useEffect(() => {
    if (!templateId) return;
    const mine = ++generation.current;
    setIsStale(result !== null);
    const timer = window.setTimeout(() => void request(mine), 250);
    return () => window.clearTimeout(timer);
    // `request` closes over `values`; re-running on a new identity is the point.
  }, [templateId, documentVersion, request]);

  // A throttled panel retries itself once the window passes, rather than
  // stranding the author on a stale view.
  useEffect(() => {
    if (retryAfter === null) return;
    const timer = window.setTimeout(() => {
      setRetryAfter(null);
      void request(++generation.current);
    }, Math.min(retryAfter, 30) * 1000);
    return () => window.clearTimeout(timer);
  }, [retryAfter, request]);

  if (!templateId) {
    return (
      <section className={stack} aria-label="Preview">
        <h2 className={subheading}>Preview</h2>
        <span className={muted}>Pick a template to see what it produces.</span>
      </section>
    );
  }

  const items = result?.items ?? [];
  const gearWeight = items
    .filter((item) => item.kind === "gear")
    .reduce((total, item) => total + (item.perUnitWeight ?? 0) * item.quantity, 0);

  return (
    <section className={stack} aria-label="Preview">
      <div className={row} style={{ justifyContent: "space-between" }}>
        <h2 className={subheading}>Preview</h2>
        {isStale || isDirty ? (
          <span className={muted}>
            {retryAfter !== null
              ? `throttled, retrying in ${retryAfter}s`
              : isDirty
                ? "your last edit is still saving"
                : "updating…"}
          </span>
        ) : null}
      </div>

      {result?.resolvedTemplateTitles && result.resolvedTemplateTitles.length > 1 ? (
        <span className={muted}>Resolves as {result.resolvedTemplateTitles.join(" → ")}</span>
      ) : null}

      <div className={stack}>
        {(result?.parameters ?? []).map((parameter) => (
          <ParameterControl
            key={parameter.key}
            parameter={parameter}
            value={values[parameter.key]}
            onChange={(value) => setValues({ ...values, [parameter.key]: value })}
          />
        ))}
        {(result?.parameters ?? []).length === 0 && parameters.length === 0 ? (
          <span className={muted}>No parameters — this template always produces the same list.</span>
        ) : null}
      </div>

      {problem ? <span className={errorText}>{problem}</span> : null}
      {(result?.errors ?? []).map((issue) => (
        <span key={`${issue.code}-${issue.message}`} className={errorText}>
          {issue.message}
        </span>
      ))}

      <div className={isStale || isDirty ? `${stack} ${stale}` : stack} aria-live="polite">
        {items.length === 0 ? (
          <span className={muted}>Nothing yet — no rule produces an item with these values.</span>
        ) : (
          kindOrder.map((kind) => {
            const inKind = items.filter((item) => item.kind === kind);
            if (inKind.length === 0) return null;
            return (
              <div key={kind} className={group}>
                <div className={groupHeader}>
                  <span>{kindTitle[kind]}</span>
                  <span>
                    {(() => {
                      const count = inKind.reduce((total, item) => total + item.quantity, 0);
                      return `${count} ${count === 1 ? "item" : "items"}`;
                    })()}
                    {kind === "gear" && gearWeight > 0 ? ` · ${gearWeight.toFixed(2)} kg` : ""}
                  </span>
                </div>
                {inKind.map((item) => (
                  <div key={item.id} className={itemRow}>
                    <span>
                      <span className={quantity}>{item.quantity}×</span> {item.title}
                    </span>
                    {item.perUnitWeight ? (
                      <span className={muted}>{(item.perUnitWeight * item.quantity).toFixed(2)} kg</span>
                    ) : null}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {(result?.warnings ?? []).length > 0 ? (
        <details>
          <summary className={muted}>
            {result?.warnings.length} warning{result?.warnings.length === 1 ? "" : "s"}
          </summary>
          <div className={stack}>
            {(result?.warnings ?? []).map((issue) => (
              <span key={`${issue.code}-${issue.message}`} className={warning}>
                {issue.message}
              </span>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}

function ParameterControl({
  parameter,
  value,
  onChange,
}: {
  readonly parameter: PreviewParameter;
  readonly value: number | boolean | string | undefined;
  readonly onChange: (value: number | boolean | string) => void;
}) {
  const current = value ?? parameter.defaultValue ?? (parameter.type === "boolean" ? false : "");
  const range =
    parameter.minimum != null || parameter.maximum != null
      ? ` (${parameter.minimum ?? "–"}…${parameter.maximum ?? "–"})`
      : "";

  // The control follows the definition: a switch for a boolean, a select when the
  // author listed allowed values, a stepper inside its constraints otherwise.
  if (parameter.type === "boolean") {
    return (
      <label className={row}>
        <input type="checkbox" checked={Boolean(current)} onChange={(event) => onChange(event.target.checked)} />
        <span>{parameter.key}</span>
      </label>
    );
  }

  if (parameter.allowed && parameter.allowed.length > 0) {
    return (
      <label className={stack}>
        <span className={muted}>{parameter.key}</span>
        <select className={field} value={String(current)} onChange={(event) => onChange(event.target.value)}>
          {parameter.allowed.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    );
  }

  const numeric = parameter.type === "integer" || parameter.type === "double";
  return (
    <label className={stack}>
      <span className={muted}>
        {parameter.key}
        {range}
      </span>
      <input
        className={field}
        type={numeric ? "number" : "text"}
        {...(numeric && parameter.minimum != null ? { min: parameter.minimum } : {})}
        {...(numeric && parameter.maximum != null ? { max: parameter.maximum } : {})}
        {...(parameter.type === "integer" ? { step: 1 } : {})}
        value={String(current)}
        onChange={(event) => onChange(numeric ? Number(event.target.value) : event.target.value)}
      />
    </label>
  );
}
